/**
 * PlannedFeaturePage — placeholder rendered inside the AppShell for
 * BRD-scoped features that have a menu entry but no full UI yet.
 *
 * The goal is honesty: instead of a blank page or a 404, the admin sees
 *   - what this section is FOR
 *   - what's ALREADY landed underneath (with links)
 *   - what's scheduled next
 * so demos and audits stay coherent while the build catches up.
 */
import { Link } from "react-router-dom";
import { CalendarClock, CheckCircle2, ArrowRight, Sparkles } from "lucide-react";

export default function PlannedFeaturePage({
  eyebrow = "Planned",
  title,
  description,
  landed = [],          // [{ label, href, note }]
  planned = [],         // [{ label, note }]
  icon: Icon = Sparkles,
}) {
  return (
    <div className="max-w-4xl mx-auto p-6 lg:p-10 space-y-8" data-testid="planned-feature-page">
      <header className="flex items-start gap-4">
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: "#0A1F441A", color: "#0A1F44" }}
        >
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: "#C9A24A" }}>
            {eyebrow}
          </div>
          <h1 className="font-display text-3xl font-semibold mt-1" style={{ color: "#0A1F44" }}>
            {title}
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl leading-relaxed">{description}</p>
        </div>
      </header>

      {landed.length > 0 && (
        <section className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6">
          <h2 className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "#0A1F44" }}>
            What&apos;s already live
          </h2>
          <ul className="mt-3 space-y-2">
            {landed.map((item) => (
              <li key={item.label} className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#065F46" }} />
                <div className="flex-1">
                  {item.href ? (
                    <Link to={item.href} className="text-[13px] font-semibold hover:underline" style={{ color: "#0A1F44" }}>
                      {item.label} <ArrowRight className="inline w-3 h-3 opacity-60" />
                    </Link>
                  ) : (
                    <span className="text-[13px] font-semibold" style={{ color: "#0A1F44" }}>
                      {item.label}
                    </span>
                  )}
                  {item.note && <div className="text-[12px] text-slate-600 leading-snug mt-0.5">{item.note}</div>}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {planned.length > 0 && (
        <section className="rounded-2xl border-2 border-dashed border-slate-200 p-6">
          <h2 className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 flex items-center gap-1.5">
            <CalendarClock className="w-3.5 h-3.5" /> Scheduled next
          </h2>
          <ul className="mt-3 space-y-2">
            {planned.map((item) => (
              <li key={item.label} className="text-[13px] text-slate-700">
                <span className="font-semibold" style={{ color: "#0A1F44" }}>· {item.label}</span>
                {item.note && <span className="text-slate-500"> — {item.note}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
